// cleanup_convert_to_typescript_file_GENERICS

/* istanbul ignore file */
/* eslint-disable no-useless-escape */
import { invert, sortBy } from 'lodash';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import emailMask from 'text-mask-addons/dist/emailMask';
import moment from 'moment-timezone';

export const KEY_CODES = {
  ENTER: 13,
  SPACE: 32
};

export const KEY_NAMES = {
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
  END: 'END',
  ENTER: 'Enter',
  HOME: 'Home',
  SPACE: ' ',
  TAB: 'Tab'
};

export const DIRECTION = {
  UP: 'up',
  DOWN: 'down'
};

export const ROUTER_BASENAME = '/dbiqp';

// eslint-disable-next-line no-control-regex
export const ASCIIChars = /^[\x00-\x7F]*$/;

export const ONE_CALCULATED_SECOND_IN_A_DAY = 1 / 86400; // js-cookie needs this calcuation since it doesn't use millis
export const TEN_CALCULATED_SECONDS_IN_A_DAY = 10 / 86400; // js-cookie needs this calcuation since it doesn't use millis
export const MAX_DAYS_IN_MONTH = 31;
export const HALF_AN_HOUR = 30;
export const AMOUNT_FORMAT = '0,0.00';
// Format the API needs for dates
export const API_DATE_FORMAT = 'YYYY-MM-DD';
// Format the API needs for datetimes
export const API_DATE_TIME_FORMAT = 'YYYY-MM-DDTkk:mm:ss.SSSZZ'; // 2019-03-08T16:05:33.333-0500
export const API_JAVA_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZZ';
export const ISO_END_OF_DAY = 'YYYY-MM-DDT11:59:59.999[Z]';
export const TIME_FORMAT = 'hh:mm A';
export const TIME_FORMAT_WITH_SECONDS = 'hh:mm:ss A';
export const DATE_FORMAT = 'MM/DD/YYYY';
export const DATE_TIME_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}`;
export const DATE_TIME_FORMAT_WITH_SECONDS = `${DATE_FORMAT} ${TIME_FORMAT_WITH_SECONDS}`;
export const DATE_DD_MMM_YYYY = 'DD MMM YYYY';
// Friday, February 15
export const DATE_MONTH_DAY_FORMAT = 'MM/DD';

export const CONTROLLED_DISBURSEMENT = {
  PRESENTMENT_DATE_LIMIT: 60
};

export const TIME_FRAME = {
  MILLISECOND: 'millisecond',
  MINUTE: 'minute',
  HOUR: 'hour',
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year'
};

export const TYPES = {
  NUMBER: 'number',
  STRING: 'string',
  DATE: 'date',
  ENUM: 'enum'
};

export const ACTIONS = Object.freeze({
  APPROVE: 'Approve',
  APPROVE_STALE_DATED: 'ApproveStaleDated',
  CANCEL: 'Cancel',
  COPY: 'Copy',
  COPY_AS_PAYMENT: 'Copy As Payment',
  COPY_AS_TEMPLATE: 'Copy As Template',
  COPY_PAYMENT: 'CopyAsPayment',
  COPY_TEMPLATE: 'CopyAsTemplate',
  CREATE: 'Create',
  DELETE: 'Delete',
  DISABLE: 'Disable',
  ENABLE: 'Enable',
  INITIATE: 'Initiate',
  MODIFY: 'Modify',
  MODIFY_REVERSAL: 'ModifyReversal',
  MODIFY_APPROVE: 'ModifyApprove',
  PROCESS: 'Process',
  REJECT: 'Reject',
  RETURN: 'Return',
  REVERSE: 'Reverse',
  VIEW: 'View',
  FAILED: 'Failed',
  ACCEPT: 'Accept',
  SEND: 'Send',
  SUBMIT_FOR_APPROVAL: 'SubmitForApproval',
  SUBMIT_MODIFIED_REVERSAL: 'SubmitModifiedReversal',
  TEMPLATE_IMPORT_REVIEW: 'TEMPLATE-IMPORT-REVIEW',
  TEMPLATE: 'TEMPLATE'
});

export const DEBOUNCE_TIME = 1000;

export const STORAGE_KEYS = {
  TOKEN: 'jwtToken',
  EMULATION_TOKEN: 'emulationToken',
  EXPIRATION_SESSION_TIME: 'sessionExpiration',
  SSO_BFS_TOKEN_DUPE: 'premierToken',
  BFS_COOKIE: 'JSESSIONID',
  EMULATION_CUSTOMER_NAME: 'emulation-customerName',
  HIDE_PASSWORD_EXPIRATION_ALERT: 'hidePasswordExpirationAlert'
};

export const USER_LAST_LOGIN = 'lastLogin';
export const LAST_LOGIN = 'LAST LOGIN: ';

export const DAYS_MONDAY = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
];
export const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

export const BUSINESS_DAYS = [
  { id: 'sunday', name: 'Sunday' },
  { id: 'monday', name: 'Monday' },
  { id: 'tuesday', name: 'Tuesday' },
  { id: 'wednesday', name: 'Wednesday' },
  { id: 'thursday', name: 'Thursday' },
  { id: 'friday', name: 'Friday' },
  { id: 'saturday', name: 'Saturday' }
];

export const DEFAULT_DATEPICKER_RANGE_PRESETS = (tz = moment.tz.guess()) => ({
  'Last 30 Days': [
    moment().tz(tz).subtract(30, 'day').startOf('day'),
    moment().tz(tz).endOf('day')
  ],
  'Last 7 Days': [moment().tz(tz).subtract(7, 'day').startOf('day'), moment().tz(tz).endOf('day')],
  'Last Month': [
    moment().tz(tz).subtract(1, 'month').startOf('month').startOf('day'),
    moment().tz(tz).subtract(1, 'month').endOf('month').endOf('day')
  ],
  'This Month': [
    moment().tz(tz).startOf('month').startOf('day'),
    moment().tz(tz).endOf('month').endOf('day')
  ],
  Today: [moment().tz(tz).startOf('day'), moment().tz(tz).endOf('day')],
  Yesterday: [
    moment().tz(tz).subtract(1, 'day').startOf('day'),
    moment().tz(tz).subtract(1, 'day').endOf('day')
  ]
});

export const codeToFieldMapping = {
  10: 'ledgerBal',
  15: 'closingBal',
  30: 'collectedBal',
  45: 'closingAvailable',
  59: 'availableBalCRS',
  60: 'availableBal',
  66: 'totalFundsAvailable'
};

export const fieldToCodeMapping = invert(codeToFieldMapping);

export const DEVICES = {
  NATIVE: 'native',
  WEB: 'web'
};

export const HEADERS = {
  CONTENT_TYPE: 'Content-Type',
  DEVICE_EXPIRES: 'DEVICE-EXPIRES',
  DEVICE_TOKENS: 'DEVICE-TOKENS',
  DOMAIN: 'X-FI-DOMAIN',
  LEAD_BANK_CODE: 'X-LEADBANK-CODE',
  TOKEN: 'Authorization',
  X_CHANNEL: 'X-CHANNEL',
  X_CM_PASSCODE: 'X-CM-PASSCODE',
  X_NEXTCM_PASSCODE: 'X-NEXTCM-PASSCODE'
};

export const CONTENT_TYPES = {
  JSON: 'application/json'
};

export const WORKSPACES = {
  ACHPOSPAY: '/ach-positive-pay',
  ALERT: '/alerts',
  BTR: '/balance-and-transactions',
  BULLETINS: '/bulletins',
  CBEADMIN: '/user-maintenance',
  CFF: '/cash-flow-forecasting',
  CHANGEPASSWORD: '/change-password',
  CHECKFREE: '/checkfree',
  CONTACTCNTER: '/contact-center-workspace',
  DASHBOARD: '/dashboard',
  DEPOSITCHCK: '/mobile-check-deposit',
  DEPOSITDETAIL: '/mobile-check-deposit-detail',
  DEPOSITHIST: '/mobile-check-deposit-history',
  ESTATEMENT: '/statements',
  FRAUD: '/fraud-control',
  LOGIN: '/login',
  MODIFYUSER: '/modify-user',
  NEWUSER: '/new-user',
  POSPAY: '/fraud-control',
  PROFILEMGMT: '/profile-management',
  PYMTCNTR: '/payments',
  PYMTTPL: '/payment-templates',
  RECURRING_TRANSFER: '/recurring-transfer',
  STOPPAYMENT: '/stop-payments',
  TRANSFER: '/transfers-workspace',
  USERS: '/user-maintenance'
};

export const WORKSPACE_ID = {
  DASHBOARD: 'DASHBOARD'
};

export const WORKFLOWS = {
  ADD_CONTACT: '/add-contact',
  ALERTS: '/alerts',
  APPROVE_PAYEE: '/approve-payee',
  ASSIGN_ALERTS: '/assign-alerts',
  BULLETINS: '/bulletins',
  COLLECT_PAYMENT: '/collect-payment',
  CONFIRM_PAYMENT_RULE: '/confirm-payment-rule',
  CONTACT_CENTER_USER: '/contact-center-user',
  COPY_ACH_TEMPLATE: `${WORKSPACES.PYMTCNTR}/ach/template-copy`,
  COPY_EMPLOYEE_PAYMENT: `${WORKSPACES.PYMTCNTR}/employee-payments/copy`,
  COPY_PAYMENT_RULE: '/copy-payment-rule',
  COPY_SB_LOAN_PAYMENT: '/loan-payment/copy',
  COPY_WIRE_PAYMENT: `${WORKSPACES.PYMTCNTR}/wires/copy`,
  COPY_WIRE_PAYMENT_AS_TEMPLATE: `${WORKSPACES.PYMTCNTR}/wires/template-copy-payment`,
  CREATE_ACH_PAYMENT: `${WORKSPACES.PYMTCNTR}/ach/create`,
  CREATE_ACH_TEMPLATE: `${WORKSPACES.PYMTCNTR}/ach/template`,
  CREATE_EMPLOYEE_PAYMENTS: `${WORKSPACES.PYMTCNTR}/employee-payments/create`,
  CREATE_ISSUES_VOIDS: '/create-issues-voids',
  CREATE_LOAN_TRANSACTION: `${WORKSPACES.PYMTCNTR}/loans/create`,
  CREATE_PAYMENT: '/create-payment',
  CREATE_PAYMENT_RULE: '/create-payment-rule',
  CREATE_PAYMENT_TEMPLATE: '/create-payment-template',
  CREATE_RECURRING_TRANSFERS: '/recurring-transfer',
  CREATE_SMALL_BUSINESS_TAX_PAYMENT: '/create-small-business-tax-payment',
  CREATE_TAX_PAYMENT: '/create-tax-payment',
  CREATE_TEMPLATE: '/create-template',
  CREATE_TRANSFERS: '/create-transfers',
  CREATE_WIRE_PAYMENT: `${WORKSPACES.PYMTCNTR}/wires/create`,
  CREATE_WIRE_TEMPLATE: `${WORKSPACES.PYMTCNTR}/wires/template`,
  DEPOSIT_ACCOUNTS: '/deposit-accounts',
  DEPOSIT_ACCOUNT_DETAILS: '/deposit-account-details',
  DUAL_CONTROL_PAYMENT_RULE: '/dual-control-payment-rule',
  INITIATE_PAYMENT: '/initiate-payment',
  INITIATE_TRANSFER: '/initiate-transfer',
  INITIATE_WIRE: `${WORKSPACES.PYMTCNTR}/wires/initiate`,
  INVESTMENT_ACCOUNTS: '/investment-accounts',
  LOAN_ACCOUNTS: '/loan-accounts',
  LOAN_PAYMENT: '/loan-payment',
  LOAN_PAYMENT_TEMPLATE: `${WORKSPACES.PYMTCNTR}/loans/template`,
  LOAN_PAYMENT_TEMPLATE_MODIFY: `${WORKSPACES.PYMTCNTR}/loans/template/modify`,
  LOAN_PAYMENT_TEMPLATE_VIEW: `${WORKSPACES.PYMTCNTR}/loans/template/view`,
  MODIFY_ACH_PAYMENT: `${WORKSPACES.PYMTCNTR}/ach/modify`,
  MODIFY_ACH_TEMPLATE: `${WORKSPACES.PYMTCNTR}/ach/template-modify`,
  MODIFY_ALERT: '/modify-alert',
  MODIFY_EMPLOYEE_PAYMENT: `${WORKSPACES.PYMTCNTR}/employee-payments/modify`,
  MODIFY_PAYMENT_RULE: '/modify-payment-rule',
  MODIFY_RECURRING_TRANSFERS: `${WORKSPACES.RECURRING_TRANSFER}/modify`,
  MODIFY_REVERSAL_ACH_PAYMENT: `${WORKSPACES.PYMTCNTR}/ach/modify-reversal`,
  MODIFY_TEMPLATE: '/modify-template',
  MODIFY_WIRE_TEMPLATE: `${WORKSPACES.PYMTCNTR}/wires/template-modify`,
  OTHER_ACCOUNTS: '/other-accounts',
  PAY_AND_ADD_RULE: '/pay-and-add-rule',
  VIEW_PAYMENT_RULE: '/view-payment-rule',
  RETURN_AND_ADD_RULE: '/return-and-add-rule',
  REVERSE_ACH_PAYMENT: `${WORKSPACES.PYMTCNTR}/ach/reverse`,
  REVIEW_TRANSFER: '/review-transfer',
  SB_LOAN_PAYMENT: '/loan-payment',
  SB_LOAN_TEMPLATE: '/loan-template',
  SEND_PAYMENT: '/send-payment',
  PRINT_ACH_PAYMENT: `${WORKSPACES.PYMTCNTR}/ach/print`,
  SSO_LOGIN: '/ssologin',
  SMALL_BUSINESS_TAX_PAYMENT: '/small-business-tax-payment',
  PRINT_CD_REPORTS: `${WORKSPACES.BTR}/controlled-disbursements/print`,
  STATEMENT_DETAILS: '/statements/online-statements/getStatement',
  PRINT_WIRE_PAYMENT: `${WORKSPACES.PYMTCNTR}/wire/print`,
  TRANSFER: '/transfer',
  TRANSFER_ACTIVITY: '/transfers-workspace/transfer-activity',
  USER_PENDING_CHANGES: '/user-pending-changes',
  VIEW_ACH_PAYMENT: `${WORKSPACES.PYMTCNTR}/ach/view`,
  VIEW_ACH_TEMPLATE: `${WORKSPACES.PYMTCNTR}/ach/template-view`,
  VIEW_ALERT: '/view-alert',
  VIEW_CRITERIA: '/view-criteria',
  VIEW_EMPLOYEE_PAYMENT: `${WORKSPACES.PYMTCNTR}/employee-payments/view`,
  VIEW_FAILED_FILE: '/view-failed-file-detail',
  VIEW_IMPORT_ISSUE: '/view-import-issue-detail',
  VIEW_LOAN_PAYMENT: '/view-loan-payment',
  VIEW_PAYMENT_RULE_MODIFICATIONS: '/view-payment-rule-modifications',
  VIEW_RECURRING_TRANSFERS: `${WORKSPACES.RECURRING_TRANSFER}/view`,
  VIEW_SB_LOAN_PAYMENT: '/loan-payment/view',
  VIEW_TEMPLATE: '/view-template',
  VIEW_WIRE_FILE_SUMMARY: '/view-wire-file-summary',
  VIEW_WIRE_PAYMENT: `${WORKSPACES.PYMTCNTR}/wires/view`,
  VIEW_WIRE_TEMPLATE: `${WORKSPACES.PYMTCNTR}/wires/template-view`
};

export const MENU_LINK_TYPES = {
  DYNAMIC_LINK: 'DYNAMIC_LINK',
  DYNAMIC_LINK_NO_SPEED_BUMP: 'DYNAMIC_LINK_NO_SPEED_BUMP',
  SSO: 'SSO',
  WORKSPACE: 'WORKSPACE'
};

export const SSO_FIELD_TYPES = {
  AN: 'text',
  PASSWORD: 'Password',
  QUERY: 'QUERY'
};

export const TRANSFER_APPROVAL_REQUIREMENT = {
  NEEDS_APPROVAL: 2,
  NOT_REQUIRED: 0,
  SELF_APPROVAL: 1
};

export const TEXT_MASKS = {
  CURRENCY: createNumberMask({
    allowDecimal: true,
    includeThousandsSeparator: true,
    prefix: ''
  }),

  DECIMAL: createNumberMask({
    allowDecimal: true,
    decimalLimit: 2,
    prefix: ''
  }),

  EMAIL: emailMask,

  INTEGER: createNumberMask({
    includeThousandsSeparator: false,
    prefix: ''
  }),

  PHONE_NUMBER: [
    '(',
    /[1-9]/,
    /\d/,
    /\d/,
    ')',
    ' ',
    /\d/,
    /\d/,
    /\d/,
    '-',
    /\d/,
    /\d/,
    /\d/,
    /\d/
  ],
  STOP_PAYMENT_CHECK_NUMBER: [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]
};

export const RECURRING_TYPE = {
  AFTER: 'After',
  MONTH: 'month',
  NOT_APPLICABLE: 'Not Applicable',
  PRIOR: 'Prior',
  WEEK: 'week'
};

export const BALANCE_TRANSACTION_ACCOUNT_TYPES = {
  DEPOSIT: 'deposit',
  DEPOSIT_AND_LOAN: 'deposit_and_loan',
  INVESTMENT: 'investment',
  LOAN: 'loan',
  TIERED_LOANS: 'tiered_loans',
  OTHER: 'other'
};

export const ACH_POSPAY_PERMISSIONS = {
  ACHPPDC: 'ACHPPDC',
  ACHPPDcsns: 'ACHPPDcsns',
  ACHPPRules: 'ACHPPRules',
  ACHPPVO: 'ACHPPVO'
};

export const ACH_POSPAY_TABS = {
  DECISIONS_HISTORY: 'decisions-history',
  PAYMENTS_RULES: 'payments-rules',
  PENDING_DECISIONS: 'pending-decisions'
};

export const CHCKISSUEMNG_TYPES = {
  PPImport: 'PPImport',
  PPIssuReport: 'PPIssuReport',
  PPManualIssu: 'PPManualIssu',
  PPManualVoid: 'PPManualVoid'
};

export const TOTAL_HEADER_VARIANTS = {
  BOT_LABELS: 'bot_labels',
  TOP_LABELS: 'top_labels'
};

export const MOMENT_CALENDAR_LOCALES = {
  EN_BALANCE_TRANSACTION: 'EN_BALANCE_TRANSACTION',
  EN_TRANSFERS: 'EN_TRANSFERS'
};

export const COMMON_NOT_PERMITTED = 'COMMON.ACCESS_NOT_PERMITTED';
export const EMULATION_ERROR_CODE = 'COMMON.USER_ACTION_NOT_PERMITTED_IN_EMULATION';
export const BTR_ACCOUNTS_NOT_PERMITTED = 'BTR.ACCOUNTS.NOT.PERMITTED';
export const LOANS_ACCOUNTS_NOT_PERMITTED = 'LOANS.ACCOUNTS.NOT.PERMITTED';
export const STATEMENTS_NOT_FOUND = 'STATEMENTS_NOT_FOUND';

export const PAYMENT_METHODS = {
  ACH: 'ach',
  WIRE: 'wire'
};

export const PAYMENT_TYPES = {
  ACH: {
    EMP_CO: 'EMP+CO',
    IAT_CO: 'IAT+CO',
    IAT_DO: 'IAT+DO',
    REGULAR: 'Regular'
  },
  COLLECT: {
    COLLECT: 'Collection'
  },
  LOAN: {
    DRAW: 'DRAWS',
    LOAN_DRAW: 'Loan Draw',
    LOAN_PAYMENT: 'Loan Payment',
    PAYMENT: 'PAYMENTS'
  },
  TAX: {
    PAYMENT: 'TXP+CO',
    TAX_PAYMENT: 'Tax Payment'
  },
  WIRE: {
    DOMESTIC: 'Domestic',
    EXPEDITED: 'Expedited',
    INTERNATIONAL: 'International',
    INTERNATIONAL_WIRE: 'International Wire'
  }
};

export const PAYMENT_GROUPS = Object.keys(PAYMENT_TYPES);

export const SMB_LOAN_PAYMENT_TYPES = {
  DRAW: 'draw',
  PAYMENT: 'payment'
};

export const ACCOUNT_BALANCE_STATUS = {
  OUTSTANDING: 'Outstanding',
  AVAILABLE: 'Available'
};

export const PLACEHOLDER_DEFAULT = {
  primaryColor: '#d9d9d9',
  secondaryColor: '#ecebeb',
  spacing: 125,
  speed: 2
};

export const PLACEHOLDER = {
  DEFAULT: PLACEHOLDER_DEFAULT,
  INPUT: {
    ...PLACEHOLDER_DEFAULT,
    height: 25,
    labelHeight: 10,
    width: 150
  },
  TABLE: {
    ...PLACEHOLDER_DEFAULT,
    cellHeight: 6,
    cellSpacing: 125,
    cellWidth: 100,
    ddHeight: 7,
    ddWidth: 75,
    height: 25,
    width: 1060
  }
};

export const MATCHERS = {
  ALLOWED_CHAR_REGEX: ASCIIChars,
  ALPHA_CAPS: /^[A-Z]*$/,

  ALPHA_NUM: /^[a-z0-9]*$/i,

  // https://emailregex.com/
  // https://www.ietf.org/rfc/rfc5322.txt
  EMAIL:
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,

  EMAIL_AT_DOT: /.+@.+\..+/i,

  NUMERIC: /^[0-9]*$/i,
  PHONE: /[\d]{10}([,]{1,9}[\d]{1,10})?/
};

export const PAYMENT_ACTIONS = {
  CREATE_PAYMENT: 'create-payment',
  CREATE_TEMPLATE: 'create-template',
  MODIFY_PAYMENT: 'modify-payment',
  MODIFY_TEMPLATE: 'modify-template',
  MODIFY_REVERSAL: 'modify-reversal',
  VALIDATE_PAYMENT: 'validate-payment',
  APPROVE: 'approve',
  DELETE: 'delete',
  REJECT: 'reject',
  SEND: 'send',
  COLLECT: 'collect',
  CREATE: 'create',
  INITIATE: 'initiate',
  CONFIRM: 'confirm',
  COPY: 'copy',
  VIEW: 'view',
  MODIFY: 'modify',
  EMPLOYEE: 'employee',
  TEMPLATE: 'template',
  TEMPLATE_MODIFY: 'template-modify',
  TEMPLATE_VIEW: 'template-view',
  TEMPLATE_COPY: 'template-copy',
  REVERSE: 'reverse'
};

export const AMOUNT_WIDTH = 306;

export const LIMIT_TYPES = {
  ACH: 'achLimits',
  BOOK_TRANSFER: 'bookTransferLimits',
  LOAN: 'loanLimits',
  WIRE: 'wireLimits'
};

export const RCK_LIMIT = 2499.99;

export const ADDENDA_TYPES = Object.freeze({
  // Tax specific types
  FORM_720: '33',

  FORM_941: '20',

  FREEFORM: '32',

  MISC_CHILD_SUPPORT: '99',

  NO: 'no',

  NOTE_SPECIAL_INSTRUCTION: '98',

  OTHER_TAX: '22',

  REFERENCE_ID: '96',
  REMITTANCE_ADVICE: '95',
  TRACE: '97'
});

export const ADDENDA_MAX_AMT = 99999999.99;

export const sortAccounts = (accounts) =>
  sortBy(
    (accounts || []).map((acc) => ({
      ...acc,
      accNameSortParameter: (
        acc.accountNickName ||
        acc.accountNickname ||
        acc.nickname ||
        acc.accountName ||
        acc.name ||
        acc.optionName ||
        ''
      ).toLowerCase(),
      accNumberSortParameter: (acc.accountNumber || acc.number || '')
        .toString()
        .split('*')
        .join('')
    })),
    ['accNameSortParameter', 'accNumberSortParameter']
  );

export const serviceMessageTypes = {
  DGBMRDC: 'DGBMRDC'
};

export const challengeTypes = {
  EMAIL: 'EMAIL',
  OTP: 'OTP',
  RADIUS: 'RADIUS',
  RADIUS_UNAVAILABLE: 'RADIUS_UNAVAILABLE',
  SMS: 'SMS',
  VIP_ACTIVATION: 'VIP_ACTIVATION',
  VIP_CHALLENGE: 'VIP_CHALLENGE',
  VIP_UNAVAILABLE: 'VIP_UNAVAILABLE',
  VOICE: 'VOICE'
};

export const transactionStatus = {
  ACTIVE: 'ACTIVE',
  FROZEN: 'FROZEN',
  PRENOTE: 'PRENOTE'
};

export const depositAndLoan = 'deposit_and_loan';

export const wires = {
  ERROR: 'error',
  UPDATEDATEANDAPPROVE: 'updateDateAndApprove'
};

export const PAYMENT_TABS = {
  PAYMENTS: 'payments',
  TEMPLATES: 'payment-templates'
};

export const NOTIFICATION_TYPES = {
  ACHPASSTHRU: 'ACHPASSTHRU',
  ACH_POSITIVE_PAY: 'ACH_POSITIVE_PAY',
  ACH_REVERSAL: 'ACH_REVERSAL',
  ALERTS: 'ALERTS',
  BULLETINS: 'BULLETINS',
  PAYEES: 'PAYEES',
  PAYMENTS: 'PAYMENTS',
  POSITIVE_PAY: 'POSITIVE_PAY',
  SIMPLIFIED_PAYMENTS: 'SIMPLIFIED_PAYMENTS',
  TEMPLATES: 'TEMPLATES',
  TRANSFERS: 'TRANSFERS',
  USERS: 'USERS'
};

// Column variant definition
export const CURRENCY = 'currency';

export const CURRENCY_TYPES = {
  BERMUDAN_DOLLAR: {
    currency: 'BMD',
    name: 'Bermudan Dollar'
  },
  EURO: {
    currency: 'EUR',
    name: 'Euro'
  },
  MEXICAN_PESO: {
    currency: 'MXN',
    name: 'Mexican Peso'
  },
  US_DOLLAR: {
    currency: 'USD',
    name: 'US Dollar'
  }
};

export const US = 'US';

export const PAYMENT_STATUS = {
  REQUIRES_MY_APPROVAL: 'requires my approval',
  REQUIRES_OTHERS_APPROVAL: 'Requires others approval',
  SAME_DAY: 'same day',
  STALE_DATED: 'stale dated',
  AVAILABLE_FOR_USE: 'Available for use'
};

export const PAYMENT_CATS = {
  ACH: 'ACH',
  WIRE: 'Wire',
  LOAN: 'Loan'
};

export const PAYMENT_COLS = {
  NEW_TRANSACTION_DATE: 'newTransactionDt'
};

// electronic reports
export const ELECTRONIC_REPORTS = {
  ERD_REPORT_TYPES_NOT_FOUND: 'ERD_REPORT_TYPES_NOT_FOUND'
};

export const DATE_LABELS = {
  month_to_date: 'Month To Date',
  previous_business_day: 'Previous Business Day',
  previous_month: 'Previous Month',
  quarter_to_date: 'Quarter To Date',
  today: 'Today',
  customRange: 'Custom Range'
};

export const ACCOUNT_TYPES = {
  BOTH: 'BOTH',
  CREDIT: 'CREDIT',
  DEBIT: 'DEBIT'
};

// marketing messages iframe
export const IFRAME_SIZE_CONSTANTS = {
  BREAKPOINT600: 600,
  BREAKPOINT960: 960,
  DEFAULT_NAVBAR_WIDTH: 68,
  MAX_HEIGHT: 512,
  MIN_HEIGHT: 280,
  NATIVE_PADDING: 32,
  SINGLE_MIN_HEIGHT: 10,
  WEB_PADDING: 120,
  ZERO_NAVBAR_WIDTH: 0
};

export const TAXFORMTYPES = {
  FEDERAL: 'FEDERAL',
  STATE: 'STATE'
};

export const LOGIN_HELP = 'login_help';

export const DATE = 'DATE';

export const ALERTVARIATIONS = {
  ERROR: 'error',
  INFO: 'info',
  POSITIVE: 'positive',
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  SUCCESS: 'success',
  WARNING: 'warning'
};

export const ALERT_THRESHOLDS = {
  ANY: 'Any',
  BETWEEN: 'Between',
  EQUAL_TO: 'Equal To',
  GREATER_THAN: 'Greater Than',
  LESS_THAN: 'Less Than'
};

export const PAYMENT_OPTIONS = {
  CHECKING: 'checking',
  LOAN: 'loan'
};

export const API_ERROR = {
  PAYMENT_PREFUND_EXCEEDED_DAILY_LIMIT: 'PAYMENT.PREFUND_EXCEEDED_DAILY_LIMIT',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED'
};

export const SIMPLIFIED_PAYMENT_TYPES = {
  ACH: 'ach',
  DOMESTIC: 'domestic',
  EMPLOYEE: 'employee',
  FED: 'FED',
  INTERNATIONAL: 'international',
  LOAN_DRAW: 'loan-draw',
  LOAN_PAYMENT: 'loan-payment',
  TAX_PAYMENT: 'tax-payment'
};

export const PAYMENT_TEMPLATE_TYPES = {
  DOMESTIC: 'Domestic',
  INTERNATIONAL: 'International'
};

export const TEMPLATE_TYPES = {
  WIRE_TEMPLATES: 'wireTemplates',
  PAYMENTS_TEMPLATES: 'paymentTemplates'
};

export const PAYMENT_TEMPLATE_ACTIONS = {
  CREATE: 'create',
  DELETE: 'delete',
  MODIFIED: 'modified'
};

export const PAYMENT_SCREEN_TABS = {
  ACHIMPORT: 'ach-import',
  IMPORT: 'import-manager',
  PAYMENT_MAPS: 'payment-maps',
  PAYMNT: 'payments',
  TMPLTS: 'payment-templates'
};
export const OFFSET = {
  T: 'T',
  B: 'B'
};

export const INFUSE = {
  ENABLED: 'ENABLED',
  DISABLED: 'DISABLED',
  IS_UNDEFINED: 'IS_UNDEFINED'
};

export const INFUSE_LOGIN = ['AFTER_LOGIN_MANDATORY', 'AFTER_LOGIN_OPTIONAL'];

export const COLUMN_TYPES = {
  AMOUNT: 'amount',
  DATE: 'date',
  ENUM: 'enum',
  MULTISELECT: 'multiselect',
  NUMBER: 'number',
  TEXT: 'text',
  STRING: 'string',
  RANGE: 'range',
  ROW_SELECTOR: 'rowSelector'
};

export const SSO_LIST_TYPES = {
  NONE: 'None',
  SINGLE: 'Single'
};

// added this const for general needs of Status
// in the future, for your needs, you can add 'Approved', 'Pending', etc.
export const ITEM_GRID_STATUS = {
  APPROVED: 'Approved',
  OK: 'OK',
  REMOVED: 'Removed',
  SUCCESS: 'Success',
  PENDING: 'Pending'
};

export const RUNNING_TOTAL_COLUMN = 'runningTotal';

export const ACCOUNT_SUMMARY_TABS = {
  FINANCIALOVERVIEW: 'financialOverview',
  ALLACCOUNTS: 'allAccounts',
  BALANCES: 'balances',
  ACCOUNTACTIVITY: 'accountActivity',
  EXPORT: 'export',
  WIREDETAIL: 'wireDetail'
};

export const TRANSACTION_TYPES = {
  COLLECT: 'COLLECT',
  SEND: 'SEND',
  CREDIT: 'CREDIT',
  DEBIT: 'DEBIT'
};

export const PAGE_SIZE = 'pageSize';
export const PAGE = 'page';
export const DEFAULT_PER_PAGE = 25;

export const FIELD_DEFAULT_MAPPING = {
  entryDay: 'defaultMaximumEntryPerDay',
  entryTxn: 'defaultMaximumEntryPerTxn',
  approvalDay: 'defaultMaximumApprovalPerDay',
  approvalTxn: 'defaultMaximumApprovalPerTxn'
};

export const SORT_PARAMS = {
  ASCENDING: 'asc',
  DESCENDING: 'desc'
};

export const FRAUD_CONTROL_ACTIONS = {
  PAY_AND_ISSUE: 'PAY_AND_ISSUE',
  PAY_AS_ISSUED: 'PAY_AS_ISSUED',
  RETURN: 'RETURN',
  PAY: 'PAY'
};

export const ACHPP_PENDING_DECISIONS_ACTIONS = {
  VIEW: 'VIEW',
  PAY_AND_ADD_RULE: 'PAY_AND_ADD_RULE',
  RETURN_AND_ADD_RULE: 'RETURN_AND_ADD_RULE'
};

export const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST'
};

export const HTTP_STATUS_CODES = {
  OK: 200,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONTENT_TOO_LARGE: 413,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  GATEWAY_TIMEOUT: 504
};

export const WIRE_ACTION_TEMPLATE = 'template';
export const OPEN = 'open';
export const CLOSED = 'closed';

export const WIRE_PAYMENT_METHODS = {
  FEDWIRE: 'FED',
  SWIFT: 'SWIFT'
};

export const APPROVAL_STATUS = {
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  REQUIRES_MY_APPROVAL: 'REQUIRES_MY_APPROVAL',
  REQUIRES_OTHERS_APPROVAL: 'REQUIRES_OTHERS_APPROVAL'
};

export const RULE_STATUS = {
  ACTIVE: 'ACTIVE',
  DELETED: 'DELETED',
  EXPIRED: 'EXPIRED',
  INACTIVE: 'INACTIVE',
  INACTIVEDC: 'INACTIVEDC',
  INUSE: 'INUSE',
  PENDING: 'PENDING'
};

export const RULE_DATA_FIELDS = {
  ORIGINATOR_NAME: 'originatorName',
  ORIGINATOR_ID: 'originatorId',
  RULE_TYPE: 'ruleType',
  ACCOUNTS: 'accounts',
  AMOUNT: 'amount',
  STANDARD_ENTRY_CLASS_CODE: 'standardEntryClassCode'
};

export const PAYMENT_SOURCE = {
  FREEFORM: 'Free form',
  IMPORT: 'import',
  TEMPLATE: 'template',
  RECURRING: 'recurring'
};

export const IMPORT_REVIEW_TABS = {
  TRANSACTIONS: 'transactions',
  ERRORS: 'errors',
  IMPORTS: 'imports'
};

// for use when setting a url path and retreiving a specific url param
export const URL_PARAM_ACTIONS = {
  COPY: 'copy',
  CREATE: 'create',
  MODIFY: 'modify',
  VIEW: 'view'
};

export const TRANSFER_ACTIONS = {
  VIEW: 'view',
  MODIFY: 'modify'
};

// for assigning to a Glu Modal 'type' prop - glu will convert to lowercase for Icon match
export const GLU_MODAL_TYPES = {
  WARNING: 'WARNING',
  INFO: 'INFO',
  ERROR: 'ERROR',
  SUCCCESS: 'SUCCESS'
};

export const TRANSFER_TABS = {
  RECURRING: 'recurringTransfers',
  TRANSFER_TEMPLATES: 'transferTemplates',
  TRANSFERS: 'transfers',
  IMPORT_MGR: 'importManager'
};

export const PAYMENT_FIELDS = {
  FROM_ACCOUNT: 'fromAccount',
  TO_ACCOUNT: 'toAccount'
};

// to/from API for determining front or back side for check/deposit images
export const CHECK_DEPOSIT_IMAGE_INDICATORS = {
  FRONT: 'F',
  BACK: 'B'
};

// warning codes
export const WARNING_CODES = {
  DUPLICATE_PAYMENT_EXISTS: 'DUPLICATE_PAYMENT_EXISTS',
  INVALID_REVERSAL: 'INVALID_REVERSAL'
};

export const WIDGET_SIZE = {
  SMALL: 'S',
  LARGE: 'L'
};

export const WIDGETS = {
  PAYMENTS: 'payments'
};

export const TIME_ZONE = {
  AMERICA_NEWYORK: 'America/New_York'
};

export const BALANCE_TYPES = {
  deposit: 'BR_BALANCE_TYPE_DESC_ALL',
  loan: 'BR_LOAN_BALANCE_TYPE_DESC_ALL',
  investment: 'BR_INVESTMENT_BALANCE_TYPE_DESC_ALL',
  other: 'BR_OTHER_BALANCE_TYPE_DESC_ALL'
};

export const BALANCE_TYPES_DETAIL = {
  deposit: 'BR_BALANCE_TYPES',
  loan: 'BR_LOAN_BALANCE_TYPES',
  investment: 'BR_INVESTMENT_BALANCE_TYPES',
  other: 'BR_OTHER_BALANCE_TYPES'
};

export const EXPORT_FILE_NAME = {
  DEPOST_ACCOUNTS_DETAIL: 'DepositAccountsDetail'
};

export const AUDIT_REPORT_FIELDS = {
  DATE_TIME: 'dateTime',
  USER_ID: 'userId',
  SESSION_ID: 'sessionId',
  SUB_SERVICE: 'subService',
  DESCRIPTION: 'description'
};

export const AG_GRID_COLUMN_FILTER_TYPES = {
  NUMBER: 'agNumberColumnFilter',
  TEXT: 'agTextColumnFilter',
  DATE: 'agDateColumnFilter',
  SET: 'agSetColumnFilter'
};

export const GRID_IDS = {
  ACH_PASS_THRU_GRID: 'ach-pass-thru-grid',
  ACH_TRANSACTIONS_GRID: 'ach-transactions-grid',
  ACH_TRANSACTIONS_TEMPLATES_GRID: 'ach-transactions-templates-grid',
  AUDIT_REPORT: 'audit-report-grid',
  INVESTMENT_ACCOUNT_DETAILS_GRID: 'investment-account-details-grid',
  OTHER_ACCOUNT_DETALS_GRID: 'other-account-details-grid',
  DEPOSIT_ACCOUNTS_GRID: 'deposit-accounts-grid',
  DUPLICATES_GRID: 'duplicates-grid',
  IMPORT_MESSAGES_GRID: 'import-messages-grid',
  INVESTMENT_ACCOUNTS_GRID: 'investment-accounts-grid',
  LOAN_ACCOUNTS_GRID: 'loan-accounts-grid',
  PAYMENTS_GRID: 'payments-grid',
  PAYMENT_TEMPLATES_GRID: 'payment-templates-grid',
  PAYMENT_TEMPLATE_CONFIRMATION_GRID: 'payment-template-confirmation-grid',
  SMALL_BUSINESS_PAYMENTS_GRID: 'small-business-payments-grid',
  TRANSACTION_DETAILS_GRID: 'transaction-details-grid',
  TRANSFERS_GRID: 'transfers-grid',
  CONTROLLED_DISBURSEMENTS_GRID: 'controlled-disbursements-grid',
  CONTACT_CENTER_LIST_VIEW_GRID: 'contact-center-list-view-grid',
  CONTACT_CENTER_USER_GRID: 'contact-center-user-grid',
  DECISIONS_HISTORY_GRID: 'decisions-history-grid',
  PENDING_DECISIONS_GRID: 'pending-decisions-grid',
  PAYEE_DIRECTORY_GRID: 'payee-directory-grid',
  CUSTOM_REPORTING_SAVED_REPORTS_GRID: 'custom-reporting-saved-reports-grid',
  FINANCIAL_OVERVIEW: {
    DEPOSIT: 'financial-overview-deposit-grid',
    INVESTMENT: 'financial-overview-investment-grid',
    LOAN: 'financial-overview-loan-accounts-grid',
    OTHER: 'financial-overview-other-grid'
  },
  BALANCE_TRANSACTION_SEARCH_DETAILS_GRID: 'balance-transaction-search-details-grid',
  TIERED_LOANS_ACCOUNTS_GRID: 'tiered-loan-accounts-grid'
};

export const MULTI_ACH_INITIATE = {
  EXCEED_CUST_OVERALL_LIMIT: 'EXCEED_CUST_OVERALL_LIMIT',
  EXCEED_USER_OVERALL_LIMIT: 'EXCEED_USER_OVERALL_LIMIT',
  EXCEED_CUST_COMPANY_LIMIT: 'EXCEED_CUST_COMPANY_LIMIT',
  EXCEED_USER_COMPANY_LIMIT: 'EXCEED_USER_COMPANY_LIMIT'
};

export const DECISION_TYPES = {
  AUTOPAY: 'AUTOPAY',
  AUTORETURN: 'AUTORETURN',
  CUTOFF_PAY: 'CUTOFF_PAY',
  CUTOFF_RETURN: 'CUTOFF_RETURN',
  NEEDS_DECISION: 'NEEDS_DECISION',
  NEEDS_DECISION_PARTIAL_MATCH: 'NEEDS_DECISION_PARTIAL_MATCH',
  NEEDS_DECISION_ROLLBACK: 'NEEDS_DECISION_ROLLBACK',
  PAY: 'PAY',
  RETURN: 'RETURN'
};

export const OFFSET_LEVEL = {
  N: 'N'
};

export const SERVICE_CODES = {
  BK_IN: 'BKIn',
  CNCTRAM: 'CNCTRAM',
  CNCTRAPP: 'CNCTRAPP',
  CNCTRVO: 'CNCTRVO',
  PAYDIRA: 'PAYDIRA'
};

export const SESSION_STATUS = {
  ACTIVE: 'ACTIVE'
};

// for FO and BTR column mapping
export const INVESTMENT_COLUMNS = {
  ACCOUNT_NAME: { field: 'accountName' },
  ACCOUNT_NUMBER: { field: 'accountNumber' },
  AVAILABLE_BAL: { field: 'availableBal', code: '60' },
  AVAILABLE_BAL_CRS: { field: 'availableBalCRS', code: '59' },
  CLOSING_AVAILABLE: { field: 'closingAvailable', code: '45' },
  CLOSING_BAL: { field: 'closingBal', code: '15' },
  COLLECTED_BAL: { field: 'collectedBal', code: '30' },
  LEDGER_BAL: { field: 'ledgerBal', code: '10' },
  TOTAL_FUNDS_AVAILABLE: { field: 'totalFundsAvailable', code: '66' }
};

export const RESPONSES = {
  SUCCESS: 'success',
  YES: 'YES',
  NO: 'NO',
  SINGLE_CHARACTER: {
    YES: 'Y',
    NO: 'N'
  }
};

export const BALANCE_CHECK_TYPES = {
  ERROR_NO_SUBMIT: 0,
  WARNING_ALLOW_SUBMIT: 1,
  NO_NOTIFICATION_ALLOW_SUBMIT: 2
};

export const ENVIRONMENTS = {
  DEV: 'dev',
  QA: 'qa',
  QA1: 'qa1',
  QA2: 'qa2',
  QA3: 'qa3',
  QA4: 'qa4'
};

export const ALERT_EVENT_NAMES = {
  ACCOUNT_ACTIVITY: 'accountActivity',
  ACCOUNT_BALANCE: 'accountBalance',
  ACH_APPROVALS: 'ACH Approvals',
  EVENTS: 'events',
  POSITIVE_PAY: 'Positive Pay',
  REPORTS: 'reports',
  WIRE_APPROVALS: 'Wire Approvals',
  WIRES_SENT: 'Wires Sent'
};

export const ALERT_TYPE_MAP = {
  [ALERT_EVENT_NAMES.ACH_APPROVALS]: 1101,
  [ALERT_EVENT_NAMES.WIRE_APPROVALS]: 1501,
  [ALERT_EVENT_NAMES.WIRES_SENT]: 1502,
  [ALERT_EVENT_NAMES.POSITIVE_PAY]: 1601,
  [ALERT_EVENT_NAMES.ACCOUNT_ACTIVITY]: 1201,
  [ALERT_EVENT_NAMES.ACCOUNT_BALANCE]: 1202,
  [ALERT_EVENT_NAMES.REPORTS]: 1301
};

export const ALERT_NAME_FIELD_NAME_MAP = {
  'Accounts Balances': 'accountBalance',
  'Accounts Activity': 'accountActivity',
  Events: 'events',
  Reports: 'reports'
};

export const DUPLICATE_TEMPLATE = 'DUPLICATE_TEMPLATE';
export const SCHEDULED = 'Scheduled';
export const SUCCESSFUL = 'Successful';
export const FREEFORM = 'Freeform';
export const RECURRINGTRANSFERS = 'recurringTransfers';
export const RECURRINGTRANSFERTEMPLATES = 'recurringTransferTemplates';
export const FAIL = 'fail';
export const PREVIOUS = 'previous';
export const SUBMIT = 'submit';
export const APPROVE = 'approve';

export const EXPORT_FORMATS = {
  BAI: 'BAI',
  CSV: 'CSV',
  STI: 'STICSV',
  QBO: 'QBO',
  QFX: 'QFX',
  FIXED: 'fixed'
};

export const PAYEE_DIRECTORY_STATUS = {
  APPROVED: 'Approved',
  AVAILABLE_FOR_USE: 'Available for use',
  DIFFERENT_USER_REQUIRED: 'Different User Required',
  REQUIRES_MY_APPROVAL: 'Requires My Approval'
};

export const PAYEE_TYPES = {
  INDIVIDUAL: 'Individual',
  EMPLOYEE: 'Employee',
  BUSINESS: 'Business'
};

export const WEEK_OF_MONTH = {
  FIRST: 'FIRST',
  SECOND: 'SECOND',
  THIRD: 'THIRD',
  FOURTH: 'FOURTH',
  LAST: 'LAST'
};

export const INPUT_LIMIT = {
  NAME: 40,
  MEMO: 80,
  AMOUNT: 14
};

export const CHECKBOX_ACTIONS = {
  ALL: 'A',
  NONE: 'N',
  PARTIAL: 'P'
};

export const FILTER_OPERATORS = {
  OR: 'OR'
};

export const X_CHANNELS = Object.freeze({
  WEB: 'WEB2',
  MOBILE: 'MOBILE'
});

export const SYSTEM_OPTIONS = {
  FDIC_LOGO_LOGIN: '162',
  FDIC_LOGO_HEADER: '163',
  FDIC_INVESTMENT_ACCOUNT_TEXT: '842',
  FDIC_OTHER_ACCOUNT_TEXT: '841',
  SHOW_FORGOT_PASSWORD_OPTION: '843',
  DISCLAIMER_MESSAGE_OPTION_ID: '900122'
};

export const FILTERS = 'filters';
export const ON_GRID_READY = 'onGridReady';
export const BREAKPOINT700 = 700;
export const ACTIONS_COLUMN = 'actions';
export const SAVED_VIEWS = 'savedViews';
export const INVALID_DATE = 'Invalid date';
export const LOCALHOST = 'localhost';
export const DEFAULT_URL_PREFIX = '/api';
